import React from 'react'
import flow from 'lodash/fp/flow'
import trim from 'lodash/fp/trim'
import toLower from 'lodash/fp/toLower'
import split from 'lodash/fp/split'

import { Maybe } from '../types'

export const replaceLineBreaksWithBR = (text: string): JSX.Element[] => {
  const splitText = split(/\r\n|\n|\r/gm, text)

  return splitText.map((el, idx) => {
    const key = `rnd-${idx}`
    return (
      <React.Fragment key={key}>
        {idx > 0 && <br />}
        {el}
      </React.Fragment>
    )
  })
}

export const parseLinks = (text: string): React.ReactElement => {
  // First, protect email addresses by replacing them temporarily
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
  const emails: string[] = []
  const tempText = text.replace(emailRegex, match => {
    const placeholder = `__EMAIL_${emails.length}__`
    emails.push(match)
    return placeholder
  })

  // Then detect URLs in the protected text
  const urlRegex =
    /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s]*)/gi
  const parts = tempText.split(urlRegex)

  return (
    <>
      {parts.map((part, index) => {
        // Reset regex lastIndex for multiple tests
        urlRegex.lastIndex = 0
        // Generate unique key
        const uniqueKey = `${part.length}-${part.slice(0, 10)}-${index}`

        // Restore emails in this part
        let processedPart = part
        emails.forEach((email, emailIndex) => {
          processedPart = processedPart.replace(
            `__EMAIL_${emailIndex}__`,
            email
          )
        })

        // Check if this part is a URL
        if (urlRegex.test(part)) {
          let href = processedPart
          let displayText = processedPart
          // If it doesn't start with http/https, add https://
          if (
            !processedPart.startsWith('http://') &&
            !processedPart.startsWith('https://')
          ) {
            href = `https://${processedPart}`
          }
          // Clean punctuation at the end
          const cleanMatch = processedPart.replace(/[.,;:!?)]+$/, '')
          href = href.replace(/[.,;:!?)]+$/, '')
          displayText = cleanMatch
          return (
            <a
              key={`link-${uniqueKey}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {displayText}
            </a>
          )
        }
        // Regular text (with restored emails)
        return (
          <React.Fragment key={`text-${uniqueKey}`}>
            {processedPart}
          </React.Fragment>
        )
      })}
    </>
  )
}

// we cant use this functions with Maybe<string> so we need to cast it to string
export const cleanString = (text: Maybe<string> = ''): string =>
  flow(trim, toLower)(text as string)

export const textContainsEmoji = (text?: Maybe<string>): boolean => {
  if (!text) return false
  const emojiRegex =
    /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}]/u
  return emojiRegex.test(text)
}

export const smsLimit = (text?: Maybe<string>): number => {
  // more info: https://www.twilio.com/docs/glossary/what-sms-character-limit
  if (textContainsEmoji(text)) return 70
  if (text?.length && text.length > 160) return 153
  return 160
}

/**
 * Sanitizes text for SMS delivery by replacing characters that are automatically
 * converted by SMS carriers due to GSM 7-bit encoding limitations.
 *
 * SMS messages use GSM 7-bit character set which has limited character support.
 * When characters outside this set are used, they get automatically converted
 * or removed by SMS gateways including AWS SNS SMS service.
 *
 * This function simulates how the text will appear when received on mobile devices.
 *
 * References:
 * - AWS SNS SMS Character Encoding: https://docs.aws.amazon.com/sns/latest/dg/sms_preferences.html
 * - GSM 7-bit Character Set: https://en.wikipedia.org/wiki/GSM_03.38
 * - SMS Character Encoding Issues: https://www.twilio.com/docs/glossary/what-is-gsm-7-character-encoding
 *
 * @param {string} text - The original text to be sanitized
 * @returns {string} - Text as it will appear in the SMS message
 */
export const sanitizeSMSText = (text: string): string => {
  const replacements: Record<string, string> = {
    // GSM-incompatible punctuation and symbols
    '|': '@',
    '\\': '/',
    '^': '',
    '{': '(',
    '}': ')',
    '[': '(',
    ']': ')',
    '~': '-',
    '€': 'E',
    '¡': '!',
    '¿': '?',
    '«': '"',
    '»': '"',
    "'": "'",
    '"': '"',
    '–': '-',
    '—': '-',
    '…': '...',
    '£': 'GBP',
    '¥': 'YEN',
    '₹': 'INR',
    '₽': 'RUB',
    '₩': 'KRW',
    '₪': 'ILS',
    '₫': 'VND',
    '₡': 'CRC',
    '₦': 'NGN',
    '₨': 'PKR',
    '₱': 'PHP',
    '₴': 'UAH',
    '₸': 'KZT',
    '₼': 'AZN',
    '±': '+/-',
    '×': 'x',
    '÷': '/',
    '≤': '<=',
    '≥': '>=',
    '≠': '!=',
    '≈': '~',
    '°': 'deg',
    '²': '2',
    '³': '3',
    '½': '1/2',
    '¼': '1/4',
    '¾': '3/4',
    '®': '(R)',
    '©': '(C)',
    '™': '(TM)',
    '§': 'sec',
    '¶': 'P',
    '†': '+',
    '‡': '++',
    '•': '*',
    '‰': 'o/oo',
    µ: 'u',
    α: 'alpha',
    β: 'beta',
    γ: 'gamma',
    δ: 'delta',
    π: 'pi',
    Ω: 'ohm',
    // all vowels with accents
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ú: 'u',
    Á: 'A',
    É: 'E',
    Í: 'I',
    Ó: 'O',
    Ú: 'U',
    // other cases
    ñ: ' ',
    Ñ: ' ',
  }

  let sanitized = text

  // Apply all character replacements
  Object.entries(replacements).forEach(([original, replacement]) => {
    sanitized = sanitized.replaceAll(original, replacement)
  })

  return sanitized
}
